import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Ignore tiny scroll jitter so show/hide does not flicker
   * @type {number}
   */
  #scrollThreshold = 8;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Height of header-group siblings above the header (e.g. announcement bar)
   * @type {number}
   */
  #announcementHeight = 0;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (!entry?.borderBoxSize?.[0]) continue;

      if (entry.target === this) {
        const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
        document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

        // Check if the menu drawer should be hidden in favor of the header menu
        if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
          this.#updateMenuVisibility(false);
        }
      }
    }

    if (this.hasAttribute('fixed')) {
      this.#measureAnnouncementHeight();
      this.#updateFixedTopOffset();
    }
  });

  /**
   * Measures announcement / header-group sections above the header
   */
  #measureAnnouncementHeight = () => {
    const headerSection = this.closest('.header-section');
    if (!headerSection) {
      this.#announcementHeight = 0;
      return;
    }

    let height = 0;
    let sibling = headerSection.previousElementSibling;
    while (sibling) {
      if (sibling instanceof HTMLElement) {
        height += sibling.offsetHeight;
      }
      sibling = sibling.previousElementSibling;
    }
    this.#announcementHeight = height;
  };

  /**
   * Positions the fixed header below the announcement bar.
   * As the announcement scrolls away, top eases to 0.
   */
  #updateFixedTopOffset = () => {
    if (!this.hasAttribute('fixed')) return;

    const headerSection = this.closest('.header-section');
    if (!headerSection) return;

    const top = Math.max(0, this.#announcementHeight - getScrollTop());
    const value = `${top}px`;
    headerSection.style.setProperty('--header-fixed-top', value);
    this.style.setProperty('--header-fixed-top', value);
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root (skip for fixed)
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    if (!this.hasAttribute('fixed')) {
      this.#observeStickyPosition(stickyMode === 'always');
    }
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    const isFixed = this.hasAttribute('fixed');
    const scrollTop = getScrollTop();
    const delta = scrollTop - this.#lastScrollTop;

    if (isFixed) {
      this.#updateFixedTopOffset();
    }

    // Keep header visible while menus/drawers/search inside the header are open.
    // Do not use :focus-within — closing the cart restores focus to the trigger and
    // would otherwise pin stickyState to "active", blocking scroll-down hide.
    if (this.querySelector('[aria-expanded="true"], details[open], dialog[open]')) {
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
      this.#lastScrollTop = scrollTop;
      return;
    }

    // Fixed overlay: scroll position alone drives show/hide (no IntersectionObserver)
    if (isFixed && stickyMode === 'scroll-up') {
      const isAtTop = scrollTop <= 2;
      const isScrollingUp = delta < -this.#scrollThreshold;
      const isScrollingDown = delta > this.#scrollThreshold;

      if (isAtTop) {
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingDown) {
        this.dataset.stickyState = 'idle';
        this.dataset.scrollDirection = 'down';
      } else if (isScrollingUp) {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!this.#offscreen && stickyMode !== 'always') {
      if (Math.abs(delta) >= this.#scrollThreshold) {
        this.#lastScrollTop = scrollTop;
      }
      if (scrollTop <= 2) {
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      }
      return;
    }

    const headerTop = this.getBoundingClientRect().top;
    const isAtTop = headerTop >= 0 || scrollTop <= 2;
    const isScrollingUp = delta < -this.#scrollThreshold;
    const isScrollingDown = delta > this.#scrollThreshold;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else if (isScrollingDown) {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // scroll-up mode (non-fixed): slide in on up, slide out on down
    if (isAtTop) {
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
    } else if (isScrollingUp) {
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    } else if (isScrollingDown) {
      this.dataset.stickyState = 'idle';
      this.dataset.scrollDirection = 'down';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    const isFixed = this.hasAttribute('fixed');

    if (isFixed) {
      this.#measureAnnouncementHeight();
      this.#updateFixedTopOffset();

      // Re-measure announcement bar if it resizes
      const headerSection = this.closest('.header-section');
      let sibling = headerSection?.previousElementSibling;
      while (sibling) {
        if (sibling instanceof HTMLElement) {
          this.#resizeObserver.observe(sibling);
        }
        sibling = sibling.previousElementSibling;
      }
    }

    if (stickyMode) {
      // IntersectionObserver is unreliable for fixed headers (always intersecting)
      if (!isFixed) {
        this.#observeStickyPosition(stickyMode === 'always');
      } else {
        this.dataset.stickyState = 'inactive';
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    } else if (isFixed) {
      this.dataset.stickyState = 'inactive';
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
